import React from 'react';
import { ToasterWithStatusAndDesriptionParams } from '../../context-providers/ToasterProvider/ToasterProvider';
import { contractAddresses } from '../../helpers/config/contracts.config';
import { getErrorMessage } from '../../helpers/error-message.helper';
import { signatureHelper } from '../../helpers/signature.helper';
import Singleton from '../../helpers/singleton';
import { BigNumber, constants } from 'ethers';
import { Link } from 'react-router-dom';
import apiService from '../api/api.service';
import { marketsStore } from '../../stores/MarketsStore';

interface InjectedDependencies {
    usdtContract: any;
    ddpContract: any,
    userAddress: `0x${string}` | undefined;
    addToasterWithStatusAndDescription: (params: ToasterWithStatusAndDesriptionParams) => void;
    removeToaster: (id: string) => void;
    closeModal: () => void;
    signTypedData: (data: any) => Promise<string>;
}

@Singleton
export class DepositWithdrawalService {

    private usdtContract!: any;
    private ddpContract!: any;
    private userAddress?: string;
    private MAX_AMOUNT = constants.MaxUint256;
    private showToaster!: (params: ToasterWithStatusAndDesriptionParams) => void;
    private removeToaster!: (id: string) => void;
    private closeModal!: () => void;
    private signTypedData!: (data: any) => Promise<string>;

    public injectDependencies(deps: InjectedDependencies) {
        this.usdtContract = deps.usdtContract;
        this.userAddress = deps.userAddress;
        this.ddpContract = deps.ddpContract;
        this.showToaster = deps.addToasterWithStatusAndDescription;
        this.removeToaster = deps.removeToaster;
        this.closeModal = deps.closeModal;
        this.signTypedData = deps.signTypedData;
    }

    public async withdraw(amountBN: BigNumber) {
        const toasterId = this.getToasterId('withdraw', amountBN.toString());
        if (!this.userAddress || !this.signTypedData) {
            throw new Error('User address or signer is not defined');
        }

        const nonce = Math.round(Date.now() / 1000);
        const deadline = signatureHelper.getDeadlineIn7Days();
        const withdrawPendingDescription = this.getWithdrawToasterDescription();

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Withdraw USDT',
            description: withdrawPendingDescription
        });
        try {
            const signature = await signatureHelper.signWithdrawMessage(this.signTypedData, this.userAddress, amountBN, deadline, nonce);

            this.closeModal();

            const withdrawal = await apiService.accountsWithdrawalsPost({
                message: {
                    user: this.userAddress,
                    token: contractAddresses.polygon.usdt,
                    amount: amountBN.toString(),
                    nonce,
                    deadline
                },
                signature
            });

            this.removeToaster(toasterId);
            if (withdrawal.id) {
                this.showToaster({
                    id: `withdrawal:${withdrawal.id}`,
                    status: 'pending',
                    title: 'Withdraw USDT',
                    description: withdrawPendingDescription
                });
            }
        } catch (err: any) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Withdraw USDT',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async approveMaxAmount() {
        const toasterId = this.getToasterId('approve', this.MAX_AMOUNT.toString());
        const approveDescription = 'Approve Nereus to use USDT for making deposits.';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Approve USDT',
            description: approveDescription
        });
        try {
            await this.usdtContract.write.approve([contractAddresses.polygon.ddp, this.MAX_AMOUNT]);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Approve USDT',
                description: approveDescription,
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Approve USDT',
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    public async deposit(amountBN: BigNumber) {
        const toasterId = this.getToasterId('deposit', amountBN.toString());
        const title = 'Depositing USDT';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title,
            description: this.getDepositToasterDescription()
        });
        try {
            const txHash = await this.ddpContract.write.deposit([this.usdtContract.address, amountBN]);
            this.closeModal();

            this.removeToaster(toasterId);
            this.showToaster({
                id: txHash,
                status: 'pending',
                title,
                description: this.getDepositToasterDescription()
            });
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title,
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    private getToasterId(actionType: string, amount: string) {
        return JSON.stringify({
            actionType,
            amount,
            timestamp: Date.now()
        });
    }

    public getWithdrawToasterDescription() {
        const activityTabUrl = `/markets/${marketsStore.currentMarket?.marketId}/activity/withdrawals`;
        return <>It can take some time for funds to appear on your wallet. Check status in <Link to={activityTabUrl}>Activity tab.</Link></>
    }

    public getDepositToasterDescription() {
        const activityTabUrl = `/markets/${marketsStore.currentMarket?.marketId}/activity`;
        return <>Your funds will be available on your balance shortly. <Link to={activityTabUrl}>Activity tab.</Link></>
    }
}